/* eslint-disable max-lines */
import { api } from "jiffy-api";

/**
 * AddressApiService
 *
 * Handles all API interactions for address operations.
 * Provides methods for creating and updating addresses via the backend API.
 *
 * @example
 * const result = await AddressApiService.saveAddress(payload, { addressId: 123 });
 * if (result.success) {
 *   console.log("Address saved:", result.data);
 * } else {
 *   console.error("Error:", result.error);
 * }
 */
export default class AddressApiService {
  /**
   * Saves an address (creates new or updates existing)
   * Automatically routes to create or update based on addressId presence
   *
   * @param {Object} payload - Address data in API format
   * @param {string} payload.address1 - Street address
   * @param {string} payload.city - City name
   * @param {string} payload.zipcode - ZIP code
   * @param {string} payload.state_name - State abbreviation
   * @param {boolean} payload.local_delivery_address - Must be true
   * @param {string} [payload.address2] - Apt/suite/unit
   * @param {string} [payload.phone] - Phone number
   * @param {string} [payload.order_number] - Order number (for guests)
   * @param {Object} [options={}] - Additional options
   * @param {number} [options.addressId] - If provided, updates existing address
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async saveAddress(payload, options = {}) {
    const { addressId } = options;

    if (addressId) {
      return this.updateAddress(addressId, payload);
    }

    return this.createAddress(payload);
  }

  /**
   * Creates a new address
   *
   * API Endpoint: POST /api/addresses
   *
   * @param {Object} payload - Address data in API format
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async createAddress(payload) {
    try {
      const response = await api.account.addresses.createAddress(payload);

      return this.#parseResponse(response, "create");
    } catch (error) {
      return this.#handleError(error, "create");
    }
  }

  /**
   * Updates an existing address
   *
   * API Endpoint: PATCH /api/addresses/:id
   *
   * @param {number} addressId - The ID of the address to update
   * @param {Object} payload - Address data in API format
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async updateAddress(addressId, payload) {
    try {
      if (!addressId) {
        return {
          success: false,
          error: "Address ID is required for updates"
        };
      }

      const response = await api.account.addresses.updateAddress(
        { address_id: addressId },
        payload
      );

      return this.#parseResponse(response, "update");
    } catch (error) {
      return this.#handleError(error, "update");
    }
  }

  /**
   * Parses API response and extracts address data
   *
   * @param {Object} response - API response
   * @param {string} operation - Operation type ("create" or "update")
   * @returns {{success: boolean, data?: Object, error?: string}}
   * @private
   */
  static #parseResponse(response, operation) {
    if (response.status === "success") {
      return this.#parseSuccessResponse(response);
    }

    return this.#parseErrorResponse(response, operation);
  }

  /**
   * Parses successful API response
   * @private
   */
  static #parseSuccessResponse(response) {
    const address = response.address || response.addresses?.[0];

    if (address) {
      return {
        success: true,
        data: {
          id: address.id,
          address1: address.address1,
          address2: address.address2,
          city: address.city,
          state: address.state_name || address.state,
          zipcode: address.zipcode,
          customAddress: address.custom_address,
          verifiedAt: address.verified_at
        }
      };
    }

    return { success: true, data: {} };
  }

  /**
   * Parses error API response
   * @private
   */
  static #parseErrorResponse(response, operation) {
    // Priority 1: Check for field-specific validation errors first (most specific)
    if (response.errors) {
      const errorsObj = response.errors.messages || response.errors;

      // If we have field-specific errors, format and return them
      if (Object.keys(errorsObj).length > 0) {
        return { success: false, error: this.#formatValidationErrors(errorsObj) };
      }
    }

    // Priority 2: Check for general error message
    if (response.error) {
      return { success: false, error: response.error };
    }

    // Priority 3: Fallback to generic error
    return { success: false, error: `Failed to ${operation} address. Please try again.` };
  }

  /**
   * Handles API errors
   *
   * @param {Error} error - The error object
   * @param {string} operation - Operation type ("create" or "update")
   * @returns {{success: boolean, error: string}}
   * @private
   */
  static #handleError(error, operation) {
    // Priority 1: Check for field-specific validation errors first (most specific)
    if (error?.response?.data?.errors) {
      const errorsObj = error.response.data.errors.messages || error.response.data.errors;

      // If we have field-specific errors, format and return them
      if (Object.keys(errorsObj).length > 0) {
        const errorMessages = this.#formatValidationErrors(errorsObj);
        return {
          success: false,
          error: errorMessages
        };
      }
    }

    // Priority 2: Check for general error message
    if (error?.response?.data?.error) {
      return {
        success: false,
        error: error.response.data.error
      };
    }

    // Priority 3: Check if error has a message property
    if (error?.message) {
      return {
        success: false,
        error: error.message
      };
    }

    // Priority 4: Generic error message
    return {
      success: false,
      error: `An error occurred while ${operation === "create" ? "creating" : "updating"} the address. Please try again.`
    };
  }

  /**
   * Formats validation errors from API response
   * Converts { field: ["error1", "error2"] } to array of error messages
   *
   * @param {Object} errors - Errors object from API
   * @returns {string[]} Formatted error message(s)
   * @private
   */
  static #formatValidationErrors(errors) {
    if (!errors || typeof errors !== "object") {
      return ["Validation failed. Please check your input."];
    }

    const errorMessages = [];

    Object.entries(errors).forEach(([field, messages]) => {
      const fieldLabel = this.#formatFieldLabel(field);
      const messageArray = Array.isArray(messages) ? messages : [messages];

      messageArray.forEach(message => {
        // Convert message to string, handling objects
        const messageStr = this.#formatErrorMessage(message);
        if (messageStr) {
          errorMessages.push(`${fieldLabel}: ${messageStr}`);
        }
      });
    });

    return errorMessages.length > 0
      ? errorMessages
      : ["Validation failed. Please check your input."];
  }

  /**
   * Formats a single error message, handling strings, objects, and other types
   *
   * @param {*} message - Error message (string, object, etc.)
   * @returns {string} Formatted error message
   * @private
   */
  static #formatErrorMessage(message) {
    if (typeof message === "string") {
      return message.trim();
    }

    if (message && typeof message === "object") {
      // If message has a 'message' property, use that
      if (message.message) {
        return String(message.message);
      }

      // If message has an 'error' property, use that
      if (message.error) {
        return String(message.error);
      }

      // If it's an array, join the values
      if (Array.isArray(message)) {
        return message
          .map(m => this.#formatErrorMessage(m))
          .filter(Boolean)
          .join(", ");
      }

      // Try to JSON stringify the object
      try {
        return JSON.stringify(message);
      } catch {
        return String(message);
      }
    }

    return String(message);
  }

  /**
   * Formats field names for display
   * Converts "address1" to "Address 1", "state_name" to "State", etc.
   *
   * @param {string} field - Field name
   * @returns {string} Formatted field label
   * @private
   */
  static #formatFieldLabel(field) {
    const fieldLabels = {
      address1: "Address 1",
      address2: "Address 2",
      city: "City",
      state_name: "State",
      zipcode: "ZIP Code",
      phone: "Phone",
      full_name: "Full Name",
      order_number: "Order Number"
    };

    return fieldLabels[field] || field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  }
}
